import {
  CheckCircleIcon,
  AlertCircleIcon,
  Loader2Icon,
  MailIcon,
  RefreshCwIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import api from '@nebula/shared/services/api'
import { usePortalStore } from '@nebula/shared/store'

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(true)
  const [success, setSuccess] = useState(false)
  const [alreadyVerified, setAlreadyVerified] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Resend verification state
  const [showResendForm, setShowResendForm] = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  const { theme, isPortalDomain } = usePortalStore()

  const primaryColor = isPortalDomain && theme ? theme.primaryColor : '#9333ea'
  const portalName = isPortalDomain && theme ? theme.portalName : 'Nebvla Cloud'

  const backgroundStyle =
    isPortalDomain && theme
      ? theme.backgroundImageUrl
        ? {
            backgroundImage: `url(${theme.backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {
            backgroundColor: '#030712',
            backgroundImage: `radial-gradient(ellipse at 50% 40%, ${theme.secondaryColor}25 0%, transparent 60%)`,
          }
      : undefined

  // Os quatro estados desta tela compartilham a moldura; sem isto o fundo
  // temático teria de ser repetido (e sair de sincronia) quatro vezes.
  const shell = (children: React.ReactNode) => (
    <div
      className={`flex min-h-screen items-center justify-center ${isPortalDomain && theme ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
      style={backgroundStyle}
    >
      <div className="w-full max-w-md p-4">
        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
          {children}
        </div>
      </div>
    </div>
  )

  useEffect(() => {
    if (!token) {
      setError('Token de verificação não fornecido')
      setLoading(false)
      setShowResendForm(true)
      return
    }

    // Verify email on mount
    const verifyEmail = async () => {
      try {
        const response = await api.post('/api/v1/auth/verify-email', { token })
        setSuccess(true)
        setAlreadyVerified(response.data.already_verified)
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Erro ao verificar email. O link pode ter expirado.'
        setError(errorMessage)
        setShowResendForm(true)
      } finally {
        setLoading(false)
      }
    }

    verifyEmail()
  }, [token])

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resendEmail) return

    setResendLoading(true)
    setError(null)

    try {
      await api.post('/api/v1/auth/resend-verification', {
        email: resendEmail,
        frontend_url: window.location.origin,
      })
      setResendSuccess(true)
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Erro ao reenviar email. Tente novamente.'
      setError(errorMessage)
    } finally {
      setResendLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return shell(
      <>
        <div className="mb-8 flex justify-center">
          {isPortalDomain && theme?.logoUrl ? (
            <img
              src={theme.logoUrl}
              alt={portalName}
              className="max-h-24 w-auto max-w-[280px] animate-pulse object-contain"
            />
          ) : (
            <div
              className="animate-pulse rounded-full p-4"
              style={{ backgroundColor: `${primaryColor}33` }}
            >
              <MailIcon className="size-12" style={{ color: primaryColor }} />
            </div>
          )}
        </div>

        <h1 className="mb-4 text-center text-2xl font-bold text-white">Verificando seu email...</h1>

        <div className="flex justify-center">
          <Loader2Icon className="size-8 animate-spin" style={{ color: primaryColor }} />
        </div>
      </>,
    )
  }

  // Success state
  if (success) {
    return shell(
      <>
        <div className="mb-8 flex justify-center">
          <div className="rounded-full bg-green-600/20 p-4">
            <CheckCircleIcon className="size-12 text-green-500" />
          </div>
        </div>

        <h1 className="mb-4 text-center text-2xl font-bold text-white">
          {alreadyVerified ? 'Email já verificado!' : 'Email Verificado!'}
        </h1>

        <p className="mb-8 text-center text-gray-300">
          {alreadyVerified
            ? 'Seu email já havia sido verificado anteriormente. Você já pode acessar sua conta.'
            : 'Seu email foi verificado com sucesso! Agora você pode acessar todos os recursos da plataforma.'}
        </p>

        <Link
          to="/login"
          className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: primaryColor }}
        >
          Ir para o Login
        </Link>
      </>,
    )
  }

  // Resend success state
  if (resendSuccess) {
    return shell(
      <>
        <div className="mb-8 flex justify-center">
          <div className="rounded-full bg-green-600/20 p-4">
            <MailIcon className="size-12 text-green-500" />
          </div>
        </div>

        <h1 className="mb-4 text-center text-2xl font-bold text-white">Email Enviado!</h1>

        <p className="mb-8 text-center text-gray-300">
          Se o email <span style={{ color: primaryColor }}>{resendEmail}</span> estiver cadastrado,
          você receberá um novo link de verificação em alguns minutos.
        </p>

        <div className="space-y-3">
          <Link
            to="/login"
            className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Ir para o Login
          </Link>

          <button
            onClick={() => {
              setResendSuccess(false)
              setResendEmail('')
            }}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/30 py-3 font-medium text-white transition-colors hover:bg-white/10"
          >
            <RefreshCwIcon className="size-4" />
            Enviar para outro email
          </button>
        </div>
      </>,
    )
  }

  // Error state with resend form
  return shell(
    <>
      <div className="mb-8 flex justify-center">
        <div className="rounded-full bg-red-600/20 p-4">
          <AlertCircleIcon className="size-12 text-red-500" />
        </div>
      </div>

      <h1 className="mb-4 text-center text-2xl font-bold text-white">Erro na Verificação</h1>

      {error && (
        <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/20 p-4">
          <p className="text-center text-sm text-red-300">{error}</p>
        </div>
      )}

      {showResendForm && (
        <>
          <p className="mb-6 text-center text-gray-300">
            O link pode ter expirado ou ser inválido. Insira seu email para receber um novo link de
            verificação.
          </p>

          <form onSubmit={handleResendVerification} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">Email</label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:outline-none"
                style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                placeholder="seu@email.com"
              />
            </div>

            <button
              type="submit"
              disabled={resendLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {resendLoading ? (
                <>
                  <Loader2Icon className="size-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <MailIcon className="size-5" />
                  Reenviar Link de Verificação
                </>
              )}
            </button>
          </form>
        </>
      )}

      <div className="mt-6 text-center">
        <Link to="/login" className="text-sm text-gray-300 transition-colors hover:text-white">
          ← Voltar para o Login
        </Link>
      </div>
    </>,
  )
}

export default VerifyEmailPage
